import { useCallback, useEffect, useRef, useState } from "react"
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { toast } from "@/hooks/useToast"
import { Header } from "@/layouts/header"
import { uuidv4 } from "@/lib/generate-uuidv4"
import { SofiaEnvironment } from "@/sofia-environment"

type SpeechToTextLanguage = "en" | "de" | "fr" | "it"

type SpeechToTextConfig = {
  endpoint: string
  sampleRate: number
  language: SpeechToTextLanguage
  languageLocale: string
  format: "simple" | "detailed"
  deviceId: string
}

type LogEntry = {
  id: string
  timestamp: string
  type: "info" | "error" | "transcript" | "audio"
  message: string
}

type TranscriptEntry = {
  id: string
  text: string
  timestamp: number
  isFinal: boolean
}

const LANGUAGE_LOCALES: Record<SpeechToTextLanguage, string> = {
  en: "en-US",
  de: "de-DE",
  fr: "fr-FR",
  it: "it-IT",
}

const getDefaultConfig = (): SpeechToTextConfig => ({
  endpoint: SofiaEnvironment.SpeechToText.Endpoints.en,
  sampleRate: SofiaEnvironment.SpeechToText.SampleRate,
  language: "en",
  languageLocale: "en-US",
  format: "simple",
  deviceId: "default",
})

export const SpeechToTextTestPage = () => {
  const [isRecording, setIsRecording] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<"disconnected" | "connecting" | "connected">("disconnected")
  const [transcripts, setTranscripts] = useState<TranscriptEntry[]>([])
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [config, setConfig] = useState(getDefaultConfig)
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([])

  const logsEndRef = useRef<HTMLDivElement>(null)
  const transcriptsEndRef = useRef<HTMLDivElement>(null)

  const recognizerRef = useRef<SpeechSDK.SpeechRecognizer | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const workletNodeRef = useRef<AudioWorkletNode | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const pushStreamRef = useRef<SpeechSDK.PushAudioInputStream | null>(null)

  const addLog = useCallback((type: LogEntry["type"], message: string) => {
    const timestamp = new Date().toLocaleTimeString()
    const prefix = `[STT ${timestamp}]`
    switch (type) {
      case "error":
        console.error(prefix, message)
        break
      case "transcript":
        console.log(`%c${prefix} TRANSCRIPT: ${message}`, "color: #22c55e; font-weight: bold")
        break
      case "audio":
        console.debug(prefix, message)
        break
      case "info":
        console.log(prefix, message)
        break
    }
    setLogs((prev) => [...prev, { id: uuidv4(), timestamp, type, message }])
  }, [])

  // Enumerate audio input devices
  useEffect(() => {
    const enumerateDevices = async () => {
      try {
        // Request permission first to get device labels
        await navigator.mediaDevices.getUserMedia({ audio: true })
        const devices = await navigator.mediaDevices.enumerateDevices()
        const audioInputs = devices.filter(
          (device, index, self) =>
            device.kind === "audioinput" &&
            device.label !== "" &&
            self.findIndex((d) => d.groupId === device.groupId) === index,
        )
        setAudioDevices(audioInputs)
        addLog("info", `Found ${audioInputs.length} audio input devices`)
      } catch (error) {
        addLog("error", `Failed to enumerate devices: ${error instanceof Error ? error.message : String(error)}`)
      }
    }
    enumerateDevices()
  }, [addLog])

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [logs])

  useEffect(() => {
    transcriptsEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [transcripts])

  // Create speech config with extended timeouts
  const createSpeechConfig = useCallback(() => {
    const endpoint = new URL(config.endpoint)
    endpoint.searchParams.set("language", config.languageLocale)
    endpoint.searchParams.set("format", config.format)

    const speechConfig = SpeechSDK.SpeechConfig.fromEndpoint(endpoint)
    speechConfig.speechRecognitionLanguage = config.languageLocale

    // Configure timeout settings to prevent early EndOfDictation
    // speechConfig.setProperty(SpeechSDK.PropertyId.Speech_SegmentationSilenceTimeoutMs, "5000")
    speechConfig.setProperty(SpeechSDK.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs, "60000")

    return speechConfig
  }, [config])

  // Setup audio processing to feed push stream using AudioWorklet
  const setupAudioProcessing = useCallback(
    async (stream: MediaStream, pushStream: SpeechSDK.PushAudioInputStream) => {
      const audioContext = new AudioContext({ sampleRate: config.sampleRate })
      audioContextRef.current = audioContext

      // Load the audio worklet processor
      await audioContext.audioWorklet.addModule("/audio-processor.js")

      const source = audioContext.createMediaStreamSource(stream)
      const workletNode = new AudioWorkletNode(audioContext, "audio-processor")
      workletNodeRef.current = workletNode

      // Handle PCM data from the worklet
      workletNode.port.onmessage = (event) => {
        pushStream.write(event.data)
      }

      source.connect(workletNode)
      workletNode.connect(audioContext.destination)
      addLog("info", "Audio processing pipeline connected (AudioWorklet)")
    },
    [config.sampleRate, addLog],
  )

  // Setup recognizer event handlers
  const setupRecognizerEvents = useCallback(
    (recognizer: SpeechSDK.SpeechRecognizer) => {
      recognizer.recognizing = (_, e) => {
        if (e.result.text) {
          addLog("info", `Recognizing: ${e.result.text}`)
        }
      }

      recognizer.recognized = (_, e) => {
        const { result } = e
        if (result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          const { text } = result
          if (text) {
            addLog("transcript", `Doctor: ${text}`)
            setTranscripts((prev) => [...prev, { id: uuidv4(), text, timestamp: Date.now(), isFinal: true }])
          }
        } else if (result.reason === SpeechSDK.ResultReason.NoMatch) {
          const noMatchDetail = SpeechSDK.NoMatchDetails.fromResult(result)
          addLog("info", `NoMatch reason: ${SpeechSDK.NoMatchReason[noMatchDetail.reason]}`)
        }
      }

      recognizer.canceled = (_, e) => {
        addLog("info", `Canceled: ${SpeechSDK.CancellationReason[e.reason]}`)
        if (e.reason === SpeechSDK.CancellationReason.Error) {
          addLog("error", `Error: ${e.errorDetails}`)
        }
        setConnectionStatus("disconnected")
      }

      recognizer.sessionStarted = (_, e) => {
        addLog("info", `Session started: ${e.sessionId}`)
        setConnectionStatus("connected")
      }

      recognizer.sessionStopped = (_, e) => {
        addLog("info", `Session stopped: ${e.sessionId}`)
        setConnectionStatus("disconnected")
      }
    },
    [addLog],
  )

  // Start recording
  const startRecording = async () => {
    try {
      setIsRecording(true)
      setConnectionStatus("connecting")

      addLog("info", "Requesting microphone permission...")

      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false,
          sampleRate: config.sampleRate,
          ...(config.deviceId && config.deviceId !== "default" && { deviceId: { exact: config.deviceId } }),
        },
      })
      streamRef.current = micStream
      const selectedDevice = audioDevices.find((d) => d.deviceId === config.deviceId)
      addLog("info", `Microphone access granted: ${selectedDevice?.label || "Default"}`)

      const speechConfig = createSpeechConfig()
      const audioFormat = SpeechSDK.AudioStreamFormat.getWaveFormatPCM(config.sampleRate, 16, 1)
      const pushStream = SpeechSDK.AudioInputStream.createPushStream(audioFormat)
      pushStreamRef.current = pushStream
      const audioConfig = SpeechSDK.AudioConfig.fromStreamInput(pushStream)

      await setupAudioProcessing(micStream, pushStream)

      const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig)
      recognizerRef.current = recognizer
      setupRecognizerEvents(recognizer)

      recognizer.startContinuousRecognitionAsync(
        () => addLog("info", "Continuous recognition started"),
        (err) => addLog("error", `Error starting recognition: ${err}`),
      )

      toast({ description: "Transcription started (Doctor)" })
    } catch (error) {
      addLog("error", `Recording error: ${error instanceof Error ? error.message : String(error)}`)
      setIsRecording(false)
      setConnectionStatus("disconnected")
    }
  }

  // Stop recording
  const stopRecording = () => {
    addLog("info", "Stopping recording...")

    if (recognizerRef.current) {
      recognizerRef.current.stopContinuousRecognitionAsync(
        () => {
          recognizerRef.current?.close()
          recognizerRef.current = null
        },
        (err) => addLog("error", `Error stopping: ${err}`),
      )
    }
    pushStreamRef.current?.close()
    pushStreamRef.current = null
    workletNodeRef.current?.disconnect()
    workletNodeRef.current = null
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close()
    }
    audioContextRef.current = null
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null

    setIsRecording(false)
    setConnectionStatus("disconnected")
    addLog("info", "Recording stopped")
  }

  const clearAll = () => {
    setLogs([])
    setTranscripts([])
  }

  // Cleanup function for both unmount and tab close
  const cleanup = useCallback(() => {
    if (recognizerRef.current) {
      recognizerRef.current.stopContinuousRecognitionAsync(
        () => {
          recognizerRef.current?.close()
          recognizerRef.current = null
        },
        () => {
          recognizerRef.current?.close()
          recognizerRef.current = null
        },
      )
    }
    pushStreamRef.current?.close()
    pushStreamRef.current = null
    workletNodeRef.current?.disconnect()
    workletNodeRef.current = null
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close()
    }
    audioContextRef.current = null
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  // Handle browser tab close (beforeunload)
  useEffect(() => {
    const handleBeforeUnload = () => {
      cleanup()
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
      cleanup()
    }
  }, [cleanup])

  const getStatusColor = () => {
    switch (connectionStatus) {
      case "connected":
        return "bg-success text-on-success"
      case "connecting":
        return "bg-warning text-on-warning"
      case "disconnected":
        return "bg-critical text-on-critical"
    }
  }

  const getLogColor = (type: LogEntry["type"]) => {
    switch (type) {
      case "error":
        return "text-critical"
      case "transcript":
        return "text-success"
      case "audio":
        return "text-primary"
      case "info":
        return "text-on-surface-subdued"
    }
  }

  return (
    <>
      <Header title="Speech-to-Text Test Page (SDK)" />
      <div className="bg-background-subdued h-full overflow-y-auto">
        <div className="px-8 py-8">
          {/* Configuration */}
          <div className="bg-surface mb-6 rounded-lg p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Configuration</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                label="WebSocket Endpoint"
                value={config.endpoint}
                onChange={(e) => setConfig({ ...config, endpoint: e.target.value })}
                disabled={isRecording}
              />
              <Select
                value={config.language}
                onValueChange={(value: string) => {
                  const lang = value as SpeechToTextLanguage
                  setConfig({
                    ...config,
                    language: lang,
                    languageLocale: LANGUAGE_LOCALES[lang],
                    endpoint: SofiaEnvironment.SpeechToText.Endpoints[lang],
                  })
                }}
                disabled={isRecording}>
                <SelectTrigger>
                  <SelectValue placeholder="Language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English (US)</SelectItem>
                  <SelectItem value="de">German (Germany)</SelectItem>
                  <SelectItem value="it">Italian</SelectItem>
                  <SelectItem value="fr">French</SelectItem>
                </SelectContent>
              </Select>
              <div>
                <Select
                  value={config.deviceId}
                  onValueChange={(value: string) => setConfig({ ...config, deviceId: value })}
                  disabled={isRecording}>
                  <SelectTrigger>
                    <SelectValue placeholder="Default microphone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default</SelectItem>
                    {audioDevices.map((device) => (
                      <SelectItem key={device.deviceId} value={device.deviceId}>
                        {device.label || `Microphone ${device.deviceId.slice(0, 8)}...`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input
                label="Sample Rate (Hz)"
                type="number"
                value={config.sampleRate}
                onChange={(e) => setConfig({ ...config, sampleRate: Number(e.target.value) })}
                disabled={isRecording}
              />
              <Select
                value={config.format}
                onValueChange={(value: "simple" | "detailed") => setConfig({ ...config, format: value })}
                disabled={isRecording}>
                <SelectTrigger>
                  <SelectValue placeholder="Format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="simple">simple</SelectItem>
                  <SelectItem value="detailed">detailed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Controls */}
          <div className="mb-6 flex items-center gap-4">
            <Button onClick={isRecording ? stopRecording : startRecording} variant={isRecording ? "danger" : "primary"}>
              {isRecording ? "Stop Recording" : "Start Recording"}
            </Button>
            <Button onClick={clearAll} variant="secondary">
              Clear Logs
            </Button>
            <div className={`rounded-full px-3 py-1.5 text-xs font-medium ${getStatusColor()}`}>
              Doctor: {connectionStatus.toUpperCase()}
            </div>
          </div>

          {/* Transcripts */}
          <div className="mb-6">
            <h2 className="mb-2 text-lg font-semibold">Transcripts</h2>
            <div className="bg-background-subdued h-52 overflow-y-auto rounded-lg p-4">
              {transcripts.length === 0 ? (
                <p className="text-on-surface-subdued italic">No transcripts yet. Start recording and speak...</p>
              ) : (
                <div className="space-y-3">
                  {transcripts.map((t) => (
                    <div
                      key={t.id}
                      className="border-l-primary bg-primary-surface-subdued rounded-lg border-l-4 p-3 shadow-sm">
                      <div className="mb-1 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="bg-primary-surface text-on-primary-surface rounded-full px-2 py-0.5 text-xs font-medium">
                            Doctor
                          </span>
                          <span className="text-on-surface-subdued text-xs">
                            {new Date(t.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        {t.isFinal && (
                          <span className="bg-surface-subdued text-on-surface-subdued rounded-full px-2 py-0.5 text-xs font-medium">
                            Final
                          </span>
                        )}
                      </div>
                      <p className="text-on-primary-surface text-sm">{t.text}</p>
                    </div>
                  ))}
                </div>
              )}
              <div ref={transcriptsEndRef} />
            </div>
          </div>

          {/* Debug Logs */}
          <div>
            <h2 className="mb-2 text-lg font-semibold">Debug Logs</h2>
            <div className="bg-surface-subdued h-52 overflow-y-auto rounded-lg p-4 font-mono text-xs">
              {logs.map((log) => (
                <div key={log.id} className={`mb-1 ${getLogColor(log.type)}`}>
                  <span className="text-on-surface-subdued">[{log.timestamp}]</span> {log.message}
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
